import React from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { User } from '../../types';

interface RoutePermissionGuardProps {
  children: React.ReactNode;
  requiredPermission: string;
  // Optional ABAC context checking for dynamic rules
  abacCondition?: (user: User) => boolean;
  loadingFallback?: React.ReactNode;
}

export const GenericContentSkeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse" data-testid="generic-content-skeleton">
    {/* Title placeholder */}
    <div className="h-8 bg-theme-divider rounded-md w-48 mb-6"></div>
    {/* KPI cards placeholder */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="h-28 bg-theme-divider rounded-xl"></div>
      <div className="h-28 bg-theme-divider rounded-xl"></div>
      <div className="h-28 bg-theme-divider rounded-xl"></div>
    </div>
    {/* Data container placeholder */}
    <div className="h-64 bg-theme-divider rounded-xl"></div>
  </div>
);

export const RoutePermissionGuard: React.FC<RoutePermissionGuardProps> = ({
  children,
  requiredPermission,
  abacCondition,
  loadingFallback,
}) => {
  const { user, isLoading } = useAuth();
  const { hasPermission } = usePermissions();
  const location = ReactRouterDOM.useLocation();

  if (isLoading) {
    return (
      <div className="w-full">
        {loadingFallback || <GenericContentSkeleton />}
      </div>
    );
  }


  if (!user) {
    return <ReactRouterDOM.Navigate to="/" state={{ from: location }} replace />;
  }

  // Enforce both RBAC (permissions array) and ABAC (dynamic condition)
  const isAuthorized = hasPermission(requiredPermission) && (!abacCondition || abacCondition(user));

  if (!isAuthorized) {
    const defaultDashboardPath = user.defaultPath || '/';
    return <ReactRouterDOM.Navigate to={defaultDashboardPath} replace />;
  }

  return <>{children}</>;
};

export default RoutePermissionGuard;
